import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/authContext';
import { updateUserProfile, getUserById, getFriends } from '../services/api';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { PageHeading, Section, Heading, Text, Modal } from '../components/ui';
import BackgroundInfoModal from '../components/BackgroundInfoModal';
import FriendsModal from '../components/FriendsModal';
import { 
    getSubscriptionStatus, 
    getRecipeQuota, 
    openCustomerPortal, 
    cancelSubscription,
    createCheckoutSession,
    formatQuotaText,
    getPlanDisplayName,
    getPlanPricing
} from '../services/subscriptionService';
import AdvancedPricingTiers from '../components/AdvancedPricingTiers';

const ProfilePage = () => {
    const { user, userProfile, refreshUserProfile } = useAuth();
    const { userId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    const [profileData, setProfileData] = useState({ 
        displayName: '', 
        email: '',
        isPublic: false,
        backgroundInfo: {
            allergies: '',
            dislikes: '',
            likes: '',
            generalInfo: '',
            privacyAcknowledged: false
        },
        sessionPreferences: {
            alarm_sound: 'default',
            alarm_enabled: true,
            custom_alarm_url: null,
            auto_start_timers: true,
            show_ingredients_popup: true,
            narration_enabled: false,
            alarm_volume: 0.5 // Add volume control
        },
        agentSettings: {
            name: 'Chef',
            trigger: 'hey chef',
            enabled: true
        },
        chefFeatures: {
            reminders: false,
            recommendations: false,
            summaries: false
        },
        menuSchedulingSettings: {
            background_option: 'tomato' // 'tomato', 'lemon', 'spinach'
        }
    });
    const [originalProfileData, setOriginalProfileData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showFriendsModal, setShowFriendsModal] = useState(false);
    const [friendsCount, setFriendsCount] = useState(0);
    const [showPremiumLockPopup, setShowPremiumLockPopup] = useState(false);
    
    // Tab state
    const [activeTab, setActiveTab] = useState('profile');
    
    // Handle tab parameter from URL
    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (tabParam === 'subscription') {
            setActiveTab('subscription');
        } else {
            setActiveTab('profile');
        }
    }, [searchParams]);
    
    // Subscription state
    const [subscription, setSubscription] = useState(null);
    const [quotaInfo, setQuotaInfo] = useState(null);
    const [subscriptionLoading, setSubscriptionLoading] = useState(false);
    const [subscriptionError, setSubscriptionError] = useState(null);
    const [subscriptionSuccess, setSubscriptionSuccess] = useState(null);
    
    // Audio preview system
    const audioManagerRef = useRef({
        audioContext: null,
        oscillator: null,
        customAudio: null,
        isPlaying: false
    });
    
    const isOwnProfile = !userId || userId === user?.uid;

    useEffect(() => {
        const loadProfileData = async () => {
            try {
                setLoading(true);
                
                if (isOwnProfile && user) {
                    // For own profile, use the userProfile from auth context
                    const newProfileData = { 
                        displayName: userProfile?.display_name || userProfile?.username || user.displayName || '', 
                        email: userProfile?.email || userProfile?.email || user.email || '',
                        isPublic: userProfile?.is_public || false,
                        backgroundInfo: userProfile?.background_info || {
                            allergies: '',
                            dislikes: '',
                            likes: '',
                            generalInfo: '',
                            privacyAcknowledged: false
                        },
                        sessionPreferences: userProfile?.session_preferences || {
                            alarm_sound: 'default',
                            alarm_enabled: true,
                            custom_alarm_url: null,
                            auto_start_timers: true,
                            show_ingredients_popup: true,
                            narration_enabled: false,
                            alarm_volume: 0.5
                        },
                        agentSettings: {
                            name: userProfile?.agent_name || 'Chef',
                            trigger: userProfile?.agent_trigger || 'hey chef',
                            enabled: userProfile?.agent_enabled !== false
                        },
                        chefFeatures: {
                            reminders: userProfile?.agent_preferences?.chef_reminders || false,
                            recommendations: userProfile?.agent_preferences?.chef_recommendations || false,
                            summaries: userProfile?.agent_preferences?.chef_summaries || false
                        },
                        menuSchedulingSettings: {
                            background_option: userProfile?.menu_scheduling_settings?.background_option || localStorage.getItem('menuSchedulingBackground') || 'tomato'
                        }
                    };
                    setProfileData(newProfileData);
                    setOriginalProfileData(newProfileData);
                    
                    // Load friends count
                    try {
                        const token = await user.getIdToken();
                        const friends = await getFriends(token);
                        setFriendsCount(friends.length);
                    } catch (err) {
                        console.error('Error loading friends:', err);
                    }
                    
                    // Load subscription data for own profile
                    await fetchSubscriptionData();
                } else if (userId) {
                    // For other users, fetch from backend
                    try {
                        const data = await getUserById(userId);
                        const newProfileData = { 
                            displayName: data.display_name || data.username || '', 
                            email: data.email || '',
                            isPublic: data.is_public || false,
                            backgroundInfo: data.background_info || {
                                allergies: '',
                                dislikes: '',
                                likes: '',
                                generalInfo: '',
                                privacyAcknowledged: false
                            },
                            sessionPreferences: data.session_preferences || {
                                alarm_sound: 'default',
                                alarm_enabled: true,
                                custom_alarm_url: null,
                                auto_start_timers: true,
                                show_ingredients_popup: true,
                                narration_enabled: false,
                                alarm_volume: 0.5
                            },
                            agentSettings: {
                                name: data.agent_name || 'Chef',
                                trigger: data.agent_trigger || 'hey chef',
                                enabled: data.agent_enabled !== false
                            }
                        };
                        setProfileData(newProfileData);
                        setOriginalProfileData(newProfileData);
                    } catch (err) {
                        console.error('Failed to fetch user profile:', err);
                        setProfileData({ 
                            displayName: 'Unknown User', 
                            email: '',
                            isPublic: false,
                            backgroundInfo: {
                                allergies: '',
                                dislikes: '',
                                likes: '',
                                generalInfo: '',
                                privacyAcknowledged: false
                            },
                            sessionPreferences: {
                                alarm_sound: 'default',
                                alarm_enabled: true,
                                custom_alarm_url: null,
                                auto_start_timers: true,
                                show_ingredients_popup: true,
                                narration_enabled: false,
                                alarm_volume: 0.5
                            },
                            agentSettings: {
                                name: 'Chef',
                                trigger: 'Hey Chef',
                                enabled: true
                            }
                        });
                    }
                }
            } catch (err) {
                console.error('Failed to load profile:', err);
                setError('Failed to load user profile.');
            } finally {
                setLoading(false);
            }
        };

        if (user || userId) {
            loadProfileData();
        }
    }, [userId, user, userProfile, isOwnProfile]);

    // Refresh subscription data when subscription tab is activated
    useEffect(() => {
        if (activeTab === 'subscription' && isOwnProfile && user) {
            fetchSubscriptionData();
        }
    }, [activeTab, isOwnProfile, user]);

    // Fetch subscription data
    const fetchSubscriptionData = async () => {
        try {
            setSubscriptionLoading(true);
            setSubscriptionError(null);
            
            const [subscriptionData, quotaData] = await Promise.all([
                getSubscriptionStatus(),
                getRecipeQuota()
            ]);
            
            setSubscription(subscriptionData);
            setQuotaInfo(quotaData);
        } catch (err) {
            console.error('Failed to fetch subscription data:', err);
            setSubscriptionError('Failed to load subscription information');
        } finally {
            setSubscriptionLoading(false);
        }
    };

    // Audio preview system
    const audioManager = {
        // Stop all audio and clear all references
        stopAllAudio: () => {
            const manager = audioManagerRef.current;
            // Stop oscillator only
            if (manager.oscillator) {
                try {
                    manager.oscillator.stop();
                } catch (error) {
                    console.log('Oscillator stop error:', error);
                }
                manager.oscillator = null;
            }
            // Do NOT close audio context here
            // Only close on unmount
            if (manager.customAudio) {
                try {
                    manager.customAudio.pause();
                    manager.customAudio.currentTime = 0;
                } catch (error) {
                    console.log('Custom audio stop error:', error);
                }
                manager.customAudio = null;
            }
            manager.isPlaying = false;
        },
        // Play a sound preview with volume control
        playSound: (soundType, duration = 0.5) => {
            const manager = audioManagerRef.current;
            // Get current volume from profile data
            const volume = profileData.sessionPreferences.alarm_volume || 0.5;
            // Stop any current oscillator first
            if (manager.oscillator) {
                try {
                    manager.oscillator.stop();
                } catch (error) {
                    console.log('Oscillator stop error:', error);
                }
                manager.oscillator = null;
            }
            // Reuse or create AudioContext
            let audioContext = manager.audioContext;
            if (!audioContext || audioContext.state === 'closed') {
                audioContext = new window.AudioContext();
                manager.audioContext = audioContext;
            }
            if (audioContext.state === 'suspended') {
                audioContext.resume();
            }
            try {
                const oscillator = audioContext.createOscillator();
                const gainNode = audioContext.createGain();
                manager.oscillator = oscillator;
                manager.isPlaying = true;
                oscillator.connect(gainNode);
                gainNode.connect(audioContext.destination);
                const baseVolume = volume;
                switch (soundType) {
                    case 'default':
                        oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
                        oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
                        gainNode.gain.setValueAtTime(0.3 * baseVolume, audioContext.currentTime);
                        break;
                    case 'bell':
                        oscillator.frequency.setValueAtTime(523, audioContext.currentTime);
                        oscillator.frequency.setValueAtTime(659, audioContext.currentTime + 0.1);
                        oscillator.frequency.setValueAtTime(784, audioContext.currentTime + 0.2);
                        gainNode.gain.setValueAtTime(0.2 * baseVolume, audioContext.currentTime);
                        break;
                    case 'chime':
                        oscillator.frequency.setValueAtTime(440, audioContext.currentTime);
                        oscillator.frequency.setValueAtTime(554, audioContext.currentTime + 0.2);
                        oscillator.frequency.setValueAtTime(659, audioContext.currentTime + 0.4);
                        gainNode.gain.setValueAtTime(0.15 * baseVolume, audioContext.currentTime);
                        break;
                    case 'beep':
                        oscillator.frequency.setValueAtTime(1000, audioContext.currentTime);
                        oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.2);
                        oscillator.frequency.setValueAtTime(1000, audioContext.currentTime + 0.4);
                        gainNode.gain.setValueAtTime(0.25 * baseVolume, audioContext.currentTime);
                        break;
                    default:
                        oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
                        gainNode.gain.setValueAtTime(0.3 * baseVolume, audioContext.currentTime);
                }
                gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
                oscillator.start(audioContext.currentTime);
                oscillator.stop(audioContext.currentTime + duration);
                setTimeout(() => {
                    if (manager.oscillator === oscillator) {
                        manager.oscillator = null;
                        manager.isPlaying = false;
                    }
                }, duration * 1000 + 100);
            } catch (error) {
                console.error('Audio playback error:', error);
                if (manager.oscillator) {
                    try {
                        manager.oscillator.stop();
                    } catch (e) {
                        console.log('Error stopping oscillator:', e);
                    }
                    manager.oscillator = null;
                }
                manager.isPlaying = false;
            }
        },
        // Play custom audio from URL with volume control
        playCustomAudio: (url) => {
            const manager = audioManagerRef.current;
            // Stop any current audio first
            if (manager.customAudio) {
                try {
                    manager.customAudio.pause();
                    manager.customAudio.currentTime = 0;
                } catch (error) {
                    console.log('Custom audio stop error:', error);
                }
                manager.customAudio = null;
            }
            try {
                const audio = new Audio(url);
                manager.customAudio = audio;
                manager.isPlaying = true;
                const volume = profileData.sessionPreferences.alarm_volume || 0.5;
                audio.volume = volume;
                audio.play().catch(error => {
                    console.error('Custom audio playback error:', error);
                    manager.isPlaying = false;
                });
                audio.onended = () => {
                    manager.customAudio = null;
                    manager.isPlaying = false;
                };
            } catch (error) {
                console.error('Custom audio error:', error);
                manager.isPlaying = false;
            }
        }
    };
    // On unmount, close the AudioContext if it exists
    useEffect(() => {
        return () => {
            const manager = audioManagerRef.current;
            if (manager.audioContext && manager.audioContext.state !== 'closed') {
                manager.audioContext.close();
            }
            audioManager.stopAllAudio();
        };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        const updateData = { 
            display_name: profileData.displayName,
            is_public: profileData.isPublic,
            background_info: profileData.backgroundInfo,
            session_preferences: profileData.sessionPreferences,
            agent_name: profileData.agentSettings.name,
            agent_trigger: profileData.agentSettings.trigger,
            agent_enabled: profileData.agentSettings.enabled,
            agent_preferences: {
                chef_reminders: profileData.chefFeatures?.reminders || false,
                chef_recommendations: profileData.chefFeatures?.recommendations || false,
                chef_summaries: profileData.chefFeatures?.summaries || false
            },
            // Note: menu_scheduling_settings not yet supported in backend, using localStorage for now
        };
        
        // Save menu scheduling settings to localStorage
        if (profileData.menuSchedulingSettings?.background_option) {
            localStorage.setItem('menuSchedulingBackground', profileData.menuSchedulingSettings.background_option);
        }

        try {
            const token = await user.getIdToken();
            const result = await updateUserProfile(user.uid, updateData, token);
            
            // Refresh the user profile in auth context
            await refreshUserProfile();
            
            // Show success message with number of recipes updated
            const recipesUpdated = result.recipesUpdated || 0;
            let updateMessage = 'Profile updated successfully!';
            
            if (recipesUpdated > 0) {
                const changes = [];
                if (updateData.display_name !== originalProfileData?.displayName) {
                    changes.push('display name');
                }
                if (updateData.is_public !== originalProfileData?.isPublic) {
                    changes.push('privacy settings');
                }
                if (JSON.stringify(updateData.background_info) !== JSON.stringify(originalProfileData?.backgroundInfo)) {
                    changes.push('background info');
                }
                if (JSON.stringify(updateData.session_preferences) !== JSON.stringify(originalProfileData?.sessionPreferences)) {
                    changes.push('session preferences');
                }
                if (JSON.stringify(updateData.agent_name) !== JSON.stringify(originalProfileData?.agentSettings?.name) ||
                    JSON.stringify(updateData.agent_trigger) !== JSON.stringify(originalProfileData?.agentSettings?.trigger) ||
                    updateData.agent_enabled !== originalProfileData?.agentSettings?.enabled) {
                    changes.push('agent settings');
                }
                
                const changesText = changes.length > 0 ? changes.join(', ') : 'your changes';
                updateMessage += ` ${recipesUpdated} recipes updated with your new ${changesText}.`;
            }
            
            setSuccess(updateMessage);
            
        } catch (err) {
            console.error('Failed to update profile:', err);
            setError('Failed to update profile. Please try again.');
        }
    };

    const handleBackgroundInfoSave = (backgroundInfo) => {
        setProfileData(prev => ({
            ...prev,
            backgroundInfo
        }));
    };

    const hasBackgroundInfo = () => {
        const info = profileData.backgroundInfo;
        return info.allergies || info.dislikes || info.likes || info.generalInfo;
    };

    // Subscription handlers
    const handleUpgrade = async (planType) => {
        if (planType === subscription?.planType) {
            return;
        }

        // Disable subscription purchases for now
        if (planType === 'basic' || planType === 'unlimited') {
            setSubscriptionError('Subscription purchases are temporarily disabled while we set up our payment system. Please check back soon!');
            return;
        }

        setSubscriptionLoading(true);
        setSubscriptionError(null);

        try {
            const { url } = await createCheckoutSession(planType);
            window.location.href = url;
        } catch (err) {
            console.error('Failed to create checkout session:', err);
            setSubscriptionError(err.message || 'Failed to start upgrade process');
        } finally {
            setSubscriptionLoading(false);
        }
    };

    const handleManageBilling = async () => {
        setSubscriptionLoading(true);
        setSubscriptionError(null);

        try {
            const { url } = await openCustomerPortal();
            window.location.href = url;
        } catch (err) {
            console.error('Failed to open customer portal:', err);
            if (err.message && err.message.includes('No paid subscription found')) {
                setSubscriptionError('You don\'t have a paid subscription to manage. You can upgrade your plan above.');
            } else if (err.message && err.message.includes('Billing management is not yet available')) {
                setSubscriptionError('Billing management is not yet available. Please contact support for subscription changes.');
            } else {
                setSubscriptionError(err.message || 'Failed to open billing portal');
            }
        } finally {
            setSubscriptionLoading(false);
        }
    };

    const handleCancelSubscription = async () => {
        if (!window.confirm('Are you sure you want to cancel your subscription? You will lose access to premium features.')) {
            return;
        }

        setSubscriptionLoading(true);
        setSubscriptionError(null);

        try {
            await cancelSubscription();
            setSubscriptionSuccess('Subscription cancelled successfully');
            // Refresh subscription data
            await fetchSubscriptionData();
        } catch (err) {
            console.error('Failed to cancel subscription:', err);
            setSubscriptionError(err.message || 'Failed to cancel subscription');
        } finally {
            setSubscriptionLoading(false);
        }
    };

    // Volume slider handlers (horizontal)
    const handleVolumeSliderMouseDown = (e) => {
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percentage = Math.max(0, Math.min(1, x / rect.width));
        
        setProfileData(prev => ({
            ...prev,
            sessionPreferences: {
                ...prev.sessionPreferences,
                alarm_volume: percentage
            }
        }));

        const handleMouseMove = (e) => {
            const x = e.clientX - rect.left;
            const percentage = Math.max(0, Math.min(1, x / rect.width));
            
            setProfileData(prev => ({
                ...prev,
                sessionPreferences: {
                    ...prev.sessionPreferences,
                    alarm_volume: percentage
                }
            }));
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleVolumeSliderTouchStart = (e) => {
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        const touch = e.touches[0];
        const x = touch.clientX - rect.left;
        const percentage = Math.max(0, Math.min(1, x / rect.width));
        
        setProfileData(prev => ({
            ...prev,
            sessionPreferences: {
                ...prev.sessionPreferences,
                alarm_volume: percentage
            }
        }));

        const handleTouchMove = (e) => {
            e.preventDefault();
            const touch = e.touches[0];
            const x = touch.clientX - rect.left;
            const percentage = Math.max(0, Math.min(1, x / rect.width));
            
            setProfileData(prev => ({
                ...prev,
                sessionPreferences: {
                    ...prev.sessionPreferences,
                    alarm_volume: percentage
                }
            }));
        };

        const handleTouchEnd = () => {
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };

        document.addEventListener('touchmove', handleTouchMove);
        document.addEventListener('touchend', handleTouchEnd);
    };

    // Preview sound function
    const previewSound = () => {
        const soundType = profileData.sessionPreferences.alarm_sound;
        
        if (soundType === 'custom') {
            const customUrl = profileData.sessionPreferences.custom_alarm_url;
            if (customUrl) {
                audioManager.playCustomAudio(customUrl);
            } else {
                alert('Please enter a custom audio URL first.');
            }
        } else {
            audioManager.playSound(soundType);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-neutral-50">
                <div className="w-full px-0 py-8">
                    <div className="bg-neutral-0 hairline minimal-padding">
                        <Text>Loading profile...</Text>
                    </div>
                </div>
            </div>
        );
    }

    if (!user && isOwnProfile) {
        return (
            <div className="min-h-screen bg-neutral-50">
                <div className="w-full px-0 py-8">
                    <div className="bg-neutral-0 hairline minimal-padding">
                        <Heading level={2}>Access Required</Heading>
                        <Text>Please log in to view your profile.</Text>
                        <Link to="/login" className="nav-link">LOGIN</Link>
                    </div>
                </div>
            </div>
        );
    }

    // Color tokens for consistent styling
    const colorTokens = {
        oxfordBlue: '#1b263b',
        alabaster: '#EEEEE7',
        white: '#FFFFFF',
        richBlack: '#0d1b2a'
    };

    return (
        <div className="min-h-screen bg-neutral-50">
            <div className="w-full px-0 py-2">
                {/* Header */}
                {!isOwnProfile && (
                    <PageHeading 
                        size="hero"
                        subtitle="USER ACCOUNT SETTINGS"
                    >
                        {`${profileData.displayName}'S PROFILE`}
                    </PageHeading>
                )}

                {/* Tab Navigation */}
                {isOwnProfile && (
                    <div className="mt-1 mb-3">
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => {
                                    setActiveTab('profile');
                                    navigate(`/profile?tab=profile`, { replace: true });
                                }}
                                className={`px-4 py-1 font-mono text-sm uppercase tracking-wider transition-all duration-120 ${
                                    activeTab === 'profile'
                                        ? 'bg-neutral-900 text-neutral-0'
                                        : 'bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-50'
                                }`}
                                style={activeTab !== 'profile' ? { color: '#0D0D0D' } : {}}
                                onMouseEnter={(e) => {
                                    if (activeTab !== 'profile') {
                                        e.target.style.color = '#d8b4fe';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (activeTab !== 'profile') {
                                        e.target.style.color = '#0D0D0D';
                                    }
                                }}
                            >
                                PROFILE
                            </button>
                            <button
                                onClick={() => {
                                    setActiveTab('subscription');
                                    navigate(`/profile?tab=subscription`, { replace: true });
                                }}
                                className={`px-4 py-1 font-mono text-sm uppercase tracking-wider transition-all duration-120 ${
                                    activeTab === 'subscription'
                                        ? 'bg-neutral-900 text-neutral-0'
                                        : 'bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-50'
                                }`}
                                style={activeTab !== 'subscription' ? { color: '#0D0D0D' } : {}}
                                onMouseEnter={(e) => {
                                    if (activeTab !== 'subscription') {
                                        e.target.style.color = '#d8b4fe';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (activeTab !== 'subscription') {
                                        e.target.style.color = '#0D0D0D';
                                    }
                                }}
                            >
                                SUBSCRIPTION
                            </button>
                        </div>
                    </div>
                )}

                {/* Success/Error Messages */}
                {(success || error) && (
                    <div className="mb-6">
                        <div className="bg-neutral-0 hairline minimal-padding vspace">
                            {success && (
                                <div className="bg-green-50 p-4 hairline mb-4">
                                    <Text className="text-green-900">{success}</Text>
                                </div>
                            )}
                            {error && (
                                <div className="bg-red-50 p-4 hairline mb-4">
                                    <Text className="text-red-900">{error}</Text>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Tab Content */}
                {activeTab === 'profile' && (
                    <>
                        {/* Section 1: Profile Information */}
                        <div className="mb-6">
                            <div className="bg-neutral-0 hairline minimal-padding">
                                <Heading level={2} className="text-2xl font-medium mb-4" style={{ color: colorTokens.oxfordBlue }}>
                                    Profile Information
                                </Heading>
                                
                                <div className="flex items-start space-x-6 mb-4">
                                            {/* Profile Picture - Left */}
                                            <div className="flex-shrink-0">
                                                <div className="w-24 h-24 bg-neutral-50 hairline rounded-full flex items-center justify-center mb-2">
                                                    <span className="text-2xl font-mono text-neutral-900 opacity-60">
                                                        {profileData.displayName ? profileData.displayName.charAt(0).toUpperCase() : 'U'}
                                                    </span>
                                                </div>
                                                <div className="flex items-center space-x-4 text-sm font-mono text-neutral-900 opacity-60 mt-2">
                                                    <span>PROFILE</span>
                                                    <span>•</span>
                                                    <span>{profileData.isPublic ? 'PUBLIC' : 'PRIVATE'}</span>
                                                </div>
                                            </div>
                                            
                                            {/* Name and Description - Right */}
                                            <div className="flex-1">
                                                {/* Clickable Name */}
                                                <div className="mb-2">
                                                    {isOwnProfile ? (
                                                        <input
                                                            type="text"
                                                            value={profileData.displayName}
                                                            onChange={(e) => setProfileData(prev => ({ ...prev, displayName: e.target.value }))}
                                                            className="text-3xl font-serif bg-transparent border-none outline-none hover:bg-neutral-50 hover:px-2 hover:py-1 hover:rounded focus:bg-neutral-50 focus:px-2 focus:py-1 focus:rounded transition-all cursor-pointer"
                                                            style={{ color: colorTokens.oxfordBlue }}
                                                            placeholder="Enter your name"
                                                        />
                                                    ) : (
                                                        <Heading level={1} className="text-3xl font-serif" style={{ color: colorTokens.oxfordBlue }}>
                                                            {profileData.displayName || 'User Profile'}
                                                        </Heading>
                                                    )}
                                                </div>
                                                
                                            </div>
                                        </div>

                                <div className="border-t border-neutral-200 my-4"></div>

                                {/* Email and Privacy Settings - Two Column */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 relative">
                                            {/* Email (Read Only) */}
                                            <div>
                                                <label className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">
                                                    EMAIL (READ ONLY)
                                                </label>
                                                <input
                                                    type="email"
                                                    value={profileData.email}
                                                    disabled={true}
                                                    placeholder="Email address"
                                                    className="w-full px-3 py-2 hairline bg-neutral-50 opacity-60 cursor-not-allowed"
                                                />
                                                <Text size="sm" className="mt-1 opacity-60">
                                                    Email cannot be changed. Contact support if you need to update your email address.
                                                </Text>
                                            </div>

                                            {/* Privacy Settings */}
                                            <div>
                                                <label className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">
                                                    PRIVACY SETTINGS
                                                </label>
                                                
                                                <div className="space-y-4">
                                                    <div className="flex items-center space-x-3">
                                                        <input
                                                            type="checkbox"
                                                            id="isPublic"
                                                            checked={profileData.isPublic}
                                                            onChange={(e) => setProfileData(prev => ({ ...prev, isPublic: e.target.checked }))}
                                                            disabled={!isOwnProfile}
                                                            className="w-4 h-4"
                                                        />
                                                        <label htmlFor="isPublic" className="font-mono text-sm uppercase tracking-wider text-neutral-900">
                                                            PUBLIC PROFILE
                                                        </label>
                                                    </div>
                                                    <Text size="sm" className="opacity-60 ml-7">
                                                        When enabled, other users can view your profile and see recipes you've created.
                                                    </Text>
                                                </div>
                                            </div>
                                            
                                    {/* Vertical Divider - Hidden on mobile */}
                                    <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-neutral-200 transform -translate-x-1/2"></div>
                                </div>
                            </div>
                        </div>

                        {/* Section 2: Subscription Status */}
                        {isOwnProfile && (
                            <div className="mb-6">
                                <div className="bg-neutral-0 hairline minimal-padding">
                                    <Heading level={2} className="text-2xl font-medium mb-4" style={{ color: colorTokens.oxfordBlue }}>
                                        Subscription Status
                                    </Heading>
                                    
                                    {subscriptionLoading ? (
                                        <div className="flex items-center justify-center py-8">
                                            <Text className="text-neutral-900 opacity-60">Loading subscription information...</Text>
                                        </div>
                                    ) : subscription ? (
                                        <div className="space-y-4">
                                            <div className="bg-neutral-50 p-4 hairline">
                                                <div className="flex items-center justify-between mb-2">
                                                    <Text className="font-mono text-sm uppercase tracking-wider opacity-60">CURRENT PLAN</Text>
                                                    <Text className="text-lg font-medium">{getPlanDisplayName(subscription.planType)}</Text>
                                                </div>
                                                <div className="flex items-center justify-between mb-2">
                                                    <Text className="font-mono text-sm uppercase tracking-wider opacity-60">RECIPE LIMIT</Text>
                                                    <Text className="text-lg font-medium">Unlimited</Text>
                                                </div>
                                                {quotaInfo && (
                                                    <div className="flex items-center justify-between mb-2">
                                                        <Text className="font-mono text-sm uppercase tracking-wider opacity-60">RECIPES CREATED</Text>
                                                        <Text className="text-lg font-medium">{quotaInfo.used || 0}</Text>
                                                    </div>
                                                )}
                                                <div className="mt-3 p-3 bg-green-50 border border-green-200">
                                                    <Text className="font-sans text-xs text-green-800">
                                                        ✨ All users can create unlimited recipes! Premium features like ingredient search and grocery lists are available with premium plans.
                                                    </Text>
                                                </div>
                                                {subscription.status === 'active' && subscription.currentPeriodEnd && (
                                                    <div className="flex items-center justify-between mt-2">
                                                        <Text className="font-mono text-sm uppercase tracking-wider opacity-60">NEXT BILLING</Text>
                                                        <Text className="text-lg font-medium">
                                                            {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                                                        </Text>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Special message for admin/family/beta users */}
                                            {(userProfile?.is_admin || userProfile?.is_family || userProfile?.is_beta) && (
                                                <div className="bg-white text-black p-4 hairline">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <Text className="font-mono text-sm uppercase tracking-wider text-black">ACCOUNT TYPE</Text>
                                                        <Text className="text-lg font-medium text-black">
                                                            {userProfile?.is_admin ? 'ADMIN' : 
                                                             userProfile?.is_family ? 'FAMILY' : 
                                                             userProfile?.is_beta ? 'BETA' : ''}
                                                        </Text>
                                                    </div>
                                                    <div className="flex items-center justify-between">
                                                        <Text className="font-mono text-sm uppercase tracking-wider text-black">ACCESS</Text>
                                                        <Text className="text-lg font-medium text-black">UNLIMITED</Text>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="bg-neutral-50 p-4 hairline">
                                            <Text className="text-neutral-900 opacity-60">No subscription information available.</Text>
                                        </div>
                                    )}
                                    
                                    <div className="mt-6">
                                        <Link
                                            to="/profile?tab=subscription"
                                            className="inline-block px-4 py-2 bg-neutral-900 text-neutral-0 hairline font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                                        >
                                            MANAGE SUBSCRIPTION
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Section 3: Live Session Settings */}
                        {isOwnProfile && (
                            <div className="mb-6">
                                <div className="bg-neutral-0 hairline minimal-padding">
                                    <Heading level={2} className="text-2xl font-medium mb-4" style={{ color: colorTokens.oxfordBlue }}>
                                        Live Session Settings
                                    </Heading>
                                                
                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 relative">
                                                    {/* Alarm Sound Selection */}
                                                    <div>
                                                        <label className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">
                                                            ALARM SOUND
                                                        </label>
                                                        <div className="flex flex-col sm:flex-row gap-2">
                                                            <select
                                                                value={profileData.sessionPreferences.alarm_sound}
                                                                onChange={(e) => setProfileData(prev => ({
                                                                    ...prev,
                                                                    sessionPreferences: {
                                                                        ...prev.sessionPreferences,
                                                                        alarm_sound: e.target.value
                                                                    }
                                                                }))}
                                                                className="flex-1 px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:bg-neutral-50"
                                                            >
                                                                <option value="default">Default Beep</option>
                                                                <option value="bell">Bell</option>
                                                                <option value="chime">Chime</option>
                                                                <option value="beep">Beep</option>
                                                                <option value="custom">Custom URL</option>
                                                            </select>
                                                            <button
                                                                type="button"
                                                                onClick={previewSound}
                                                                className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:!text-purple-300 transition-colors font-mono text-sm uppercase tracking-wider whitespace-nowrap"
                                                            >
                                                                PREVIEW
                                                            </button>
                                                        </div>
                                                        <Text size="sm" className="mt-1 opacity-60">
                                                            Choose the sound that plays when timers finish during live cooking sessions.
                                                        </Text>
                                                    </div>

                                                    {/* Alarm Volume Control - Horizontal Slider */}
                                                    <div>
                                                        <label className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-3">
                                                            ALARM VOLUME
                                                        </label>
                                                        <div className="space-y-4">
                                                            <div className="flex items-center gap-3">
                                                                <span className="text-sm text-neutral-900">🔇</span>
                                                                <div 
                                                                    className="relative flex-1 h-2 bg-neutral-50 hairline cursor-pointer"
                                                                    onMouseDown={(e) => handleVolumeSliderMouseDown(e)}
                                                                    onTouchStart={(e) => handleVolumeSliderTouchStart(e)}
                                                                >
                                                                    {/* Volume track */}
                                                                    <div className="absolute inset-0 bg-neutral-50 hairline"></div>
                                                                    
                                                                    {/* Volume fill */}
                                                                    <div 
                                                                        className="absolute left-0 top-0 bottom-0 bg-neutral-900"
                                                                        style={{ 
                                                                            width: `${(profileData.sessionPreferences.alarm_volume || 0.5) * 100}%` 
                                                                        }}
                                                                    ></div>
                                                                    
                                                                    {/* Volume handle */}
                                                                    <div 
                                                                        className="absolute w-3 h-3 bg-neutral-900 rounded-full transform -translate-x-1/2 -translate-y-1/2 cursor-pointer volume-handle"
                                                                        style={{ 
                                                                            left: `${(profileData.sessionPreferences.alarm_volume || 0.5) * 100}%`,
                                                                            top: '50%'
                                                                        }}
                                                                    ></div>
                                                                </div>
                                                                <span className="text-sm text-neutral-900">🔊</span>
                                                            </div>
                                                            <div className="flex justify-center">
                                                                <span className="text-sm text-neutral-900 font-mono">
                                                                    {Math.round((profileData.sessionPreferences.alarm_volume || 0.5) * 100)}%
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <Text size="sm" className="mt-2 opacity-60">
                                                            Adjust alarm volume
                                                        </Text>
                                                    </div>

                                                    {/* Gray Vertical Divider */}
                                                    <div className="hidden lg:block absolute left-1/3 top-0 bottom-0 w-px bg-neutral-200 transform -translate-x-1/2"></div>

                                                    {/* Auto Start Timers */}
                                                    <div>
                                                        <label className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">
                                                            AUTO START TIMERS
                                                        </label>
                                                        <div className="flex items-center space-x-3">
                                                            <input
                                                                type="checkbox"
                                                                id="autoStartTimers"
                                                                checked={profileData.sessionPreferences.auto_start_timers}
                                                                onChange={(e) => setProfileData(prev => ({
                                                                    ...prev,
                                                                    sessionPreferences: {
                                                                        ...prev.sessionPreferences,
                                                                        auto_start_timers: e.target.checked
                                                                    }
                                                                }))}
                                                                className="w-4 h-4"
                                                            />
                                                            <label htmlFor="autoStartTimers" className="font-mono text-sm uppercase tracking-wider text-neutral-900">
                                                                ENABLED
                                                            </label>
                                                        </div>
                                                        <Text size="sm" className="mt-1 opacity-60">
                                                            Automatically start timers when steps begin
                                                        </Text>
                                                    </div>

                                                    {/* Gray Vertical Divider */}
                                                    <div className="hidden lg:block absolute left-2/3 top-0 bottom-0 w-px bg-neutral-200 transform -translate-x-1/2"></div>

                                                    {/* Custom Alarm URL - Full Width */}
                                                    {profileData.sessionPreferences.alarm_sound === 'custom' && (
                                                        <div className="md:col-span-2 lg:col-span-3">
                                                            <label className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">
                                                                CUSTOM ALARM URL
                                                            </label>
                                                            <input
                                                                type="url"
                                                                value={profileData.sessionPreferences.custom_alarm_url || ''}
                                                                onChange={(e) => setProfileData(prev => ({
                                                                    ...prev,
                                                                    sessionPreferences: {
                                                                        ...prev.sessionPreferences,
                                                                        custom_alarm_url: e.target.value
                                                                    }
                                                                }))}
                                                                placeholder="https://example.com/sound.mp3"
                                                                className="w-full px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:bg-neutral-50"
                                                            />
                                                            <Text size="sm" className="mt-1 opacity-60">
                                                                Enter a direct link to an audio file (MP3, WAV, etc.).
                                                            </Text>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Additional Session Toggles */}
                                                <div className="border-t border-neutral-200 mt-4 pt-4">
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        {/* Show Ingredients Popup */}
                                                        <div>
                                                            <label className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">
                                                                INGREDIENTS POPUP
                                                            </label>
                                                            <div className="flex items-center space-x-3">
                                                                <input
                                                                    type="checkbox"
                                                                    id="showIngredientsPopup"
                                                                    checked={profileData.sessionPreferences.show_ingredients_popup}
                                                                    onChange={(e) => setProfileData(prev => ({
                                                                        ...prev,
                                                                        sessionPreferences: {
                                                                            ...prev.sessionPreferences,
                                                                            show_ingredients_popup: e.target.checked
                                                                        }
                                                                    }))}
                                                                    className="w-4 h-4"
                                                                />
                                                                <label htmlFor="showIngredientsPopup" className="font-mono text-sm uppercase tracking-wider text-neutral-900">
                                                                    ENABLED
                                                                </label>
                                                            </div>
                                                            <Text size="sm" className="mt-1 opacity-60">
                                                                Show ingredients popup during sessions
                                                            </Text>
                                                        </div>

                                                        {/* Narration Enabled */}
                                                        <div>
                                                            <label className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">
                                                                NARRATION
                                                            </label>
                                                            <div className="flex items-center space-x-3">
                                                                <input
                                                                    type="checkbox"
                                                                    id="narrationEnabled"
                                                                    checked={profileData.sessionPreferences.narration_enabled}
                                                                    onChange={(e) => setProfileData(prev => ({
                                                                        ...prev,
                                                                        sessionPreferences: {
                                                                            ...prev.sessionPreferences,
                                                                            narration_enabled: e.target.checked
                                                                        }
                                                                    }))}
                                                                    className="w-4 h-4"
                                                                />
                                                                <label htmlFor="narrationEnabled" className="font-mono text-sm uppercase tracking-wider text-neutral-900">
                                                                    ENABLED
                                                                </label>
                                                            </div>
                                                            <Text size="sm" className="mt-1 opacity-60">
                                                                Enable voice narration during sessions
                                                            </Text>
                                                        </div>
                                                    </div>
                                                </div>
                                </div>
                            </div>
                        )}

                        {/* Section 4: Menu Scheduling Settings */}
                        {isOwnProfile && (
                            <div className="mb-6">
                                <div className="bg-neutral-0 hairline minimal-padding">
                                    <Heading level={2} className="text-2xl font-medium mb-4" style={{ color: colorTokens.oxfordBlue }}>
                                        Menu Scheduling Settings
                                    </Heading>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Background Option */}
                                        <div>
                                            <label className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">
                                                BACKGROUND IMAGE
                                            </label>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => {
                                                        const newOption = 'tomato';
                                                        setProfileData(prev => ({
                                                            ...prev,
                                                            menuSchedulingSettings: {
                                                                ...prev.menuSchedulingSettings,
                                                                background_option: newOption
                                                            }
                                                        }));
                                                        localStorage.setItem('menuSchedulingBackground', newOption);
                                                    }}
                                                    className={`px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all border-none ${
                                                        profileData.menuSchedulingSettings?.background_option === 'tomato'
                                                            ? 'bg-[#1b263b] text-white'
                                                            : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0'
                                                    }`}
                                                >
                                                    Tomato
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        const newOption = 'lemon';
                                                        setProfileData(prev => ({
                                                            ...prev,
                                                            menuSchedulingSettings: {
                                                                ...prev.menuSchedulingSettings,
                                                                background_option: newOption
                                                            }
                                                        }));
                                                        localStorage.setItem('menuSchedulingBackground', newOption);
                                                    }}
                                                    className={`px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all border-none ${
                                                        profileData.menuSchedulingSettings?.background_option === 'lemon'
                                                            ? 'bg-[#1b263b] text-white'
                                                            : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0'
                                                    }`}
                                                >
                                                    Lemon
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        const newOption = 'spinach';
                                                        setProfileData(prev => ({
                                                            ...prev,
                                                            menuSchedulingSettings: {
                                                                ...prev.menuSchedulingSettings,
                                                                background_option: newOption
                                                            }
                                                        }));
                                                        localStorage.setItem('menuSchedulingBackground', newOption);
                                                    }}
                                                    className={`px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all border-none ${
                                                        profileData.menuSchedulingSettings?.background_option === 'spinach'
                                                            ? 'bg-[#1b263b] text-white'
                                                            : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0'
                                                    }`}
                                                >
                                                    Spinach
                                                </button>
                                            </div>
                                            <Text size="sm" className="mt-2 opacity-60">
                                                Choose the background image for the menu scheduling calendar
                                            </Text>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Section 5: Chef Features & Background Information */}
                        {isOwnProfile && (
                            <div className="mb-6">
                                <div className="bg-neutral-0 hairline minimal-padding">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Chef Features */}
                                        <div>
                                            <Heading level={2} className="text-2xl font-medium mb-4" style={{ color: colorTokens.oxfordBlue }}>
                                                Chef Features
                                            </Heading>
                                            
                                            <div className="space-y-3">
                                                {/* Chef Reminders Toggle */}
                                                <div>
                                                    <label className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-1">
                                                        Chef Reminders
                                                    </label>
                                                    <div className="flex items-center space-x-2">
                                                        <input
                                                            type="checkbox"
                                                            id="chefReminders"
                                                            checked={profileData.chefFeatures?.reminders || false}
                                                            disabled={!subscription || subscription.planType === 'free'}
                                                            onChange={(e) => {
                                                                if (!subscription || subscription.planType === 'free') {
                                                                    setShowPremiumLockPopup(true);
                                                                    return;
                                                                }
                                                                setProfileData(prev => ({
                                                                    ...prev,
                                                                    chefFeatures: {
                                                                        ...prev.chefFeatures,
                                                                        reminders: e.target.checked
                                                                    }
                                                                }));
                                                            }}
                                                            className="w-4 h-4"
                                                        />
                                                        <label htmlFor="chefReminders" className={`font-mono text-sm uppercase tracking-wider ${(!subscription || subscription.planType === 'free') ? 'text-neutral-900 opacity-40' : 'text-neutral-900'}`}>
                                                            ENABLED
                                                        </label>
                                                    </div>
                                                    <Text size="sm" className="mt-1 opacity-60">
                                                        Get photo and check-in reminders during cooking
                                                    </Text>
                                                </div>
                                                
                                                {/* AI Tips Toggle */}
                                                <div>
                                                    <label className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-1">
                                                        AI Cooking Tips
                                                    </label>
                                                    <div className="flex items-center space-x-2">
                                                        <input
                                                            type="checkbox"
                                                            id="chefRecommendations"
                                                            checked={profileData.chefFeatures?.recommendations || false}
                                                            disabled={!subscription || subscription.planType === 'free'}
                                                            onChange={(e) => {
                                                                if (!subscription || subscription.planType === 'free') {
                                                                    setShowPremiumLockPopup(true);
                                                                    return;
                                                                }
                                                                setProfileData(prev => ({
                                                                    ...prev,
                                                                    chefFeatures: {
                                                                        ...prev.chefFeatures,
                                                                        recommendations: e.target.checked
                                                                    }
                                                                }));
                                                            }}
                                                            className="w-4 h-4"
                                                        />
                                                        <label htmlFor="chefRecommendations" className={`font-mono text-sm uppercase tracking-wider ${(!subscription || subscription.planType === 'free') ? 'text-neutral-900 opacity-40' : 'text-neutral-900'}`}>
                                                            ENABLED
                                                        </label>
                                                    </div>
                                                    <Text size="sm" className="mt-1 opacity-60">
                                                        Receive contextual tips based on past sessions
                                                    </Text>
                                                </div>
                                                
                                                {/* Chef Summaries Toggle */}
                                                <div>
                                                    <label className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-1">
                                                        Chef Summaries
                                                    </label>
                                                    <div className="flex items-center space-x-2">
                                                        <input
                                                            type="checkbox"
                                                            id="chefSummaries"
                                                            checked={profileData.chefFeatures?.summaries || false}
                                                            disabled={!subscription || subscription.planType === 'free'}
                                                            onChange={(e) => {
                                                                if (!subscription || subscription.planType === 'free') {
                                                                    setShowPremiumLockPopup(true);
                                                                    return;
                                                                }
                                                                setProfileData(prev => ({
                                                                    ...prev,
                                                                    chefFeatures: {
                                                                        ...prev.chefFeatures,
                                                                        summaries: e.target.checked
                                                                    }
                                                                }));
                                                            }}
                                                            className="w-4 h-4"
                                                        />
                                                        <label htmlFor="chefSummaries" className={`font-mono text-sm uppercase tracking-wider ${(!subscription || subscription.planType === 'free') ? 'text-neutral-900 opacity-40' : 'text-neutral-900'}`}>
                                                            ENABLED
                                                        </label>
                                                    </div>
                                                    <Text size="sm" className="mt-1 opacity-60">
                                                        Post-session Q&A for cooking reflections
                                                    </Text>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Background Information - Visible for all, editable for premium only */}
                                        <div>
                                            <Heading level={2} className="text-2xl font-medium mb-4" style={{ color: colorTokens.oxfordBlue }}>
                                                Cooking Background
                                            </Heading>
                                            
                                            <div className="space-y-3">
                                                <Text size="sm">
                                                    Share your dietary preferences, allergies, and cooking background to get more personalized recipe recommendations.
                                                </Text>
                                                
                                                {hasBackgroundInfo() && (
                                                    <div className="bg-neutral-50 p-3 hairline">
                                                        <Text size="sm" className="font-mono uppercase tracking-wider mb-2">CURRENT BACKGROUND INFO:</Text>
                                                        {profileData.backgroundInfo.allergies && (
                                                            <Text size="sm"><strong>Allergies:</strong> {profileData.backgroundInfo.allergies}</Text>
                                                        )}
                                                        {profileData.backgroundInfo.dislikes && (
                                                            <Text size="sm"><strong>Dislikes:</strong> {profileData.backgroundInfo.dislikes}</Text>
                                                        )}
                                                        {profileData.backgroundInfo.likes && (
                                                            <Text size="sm"><strong>Likes:</strong> {profileData.backgroundInfo.likes}</Text>
                                                        )}
                                                    </div>
                                                )}
                                                
                                                {(!subscription || subscription.planType === 'free') ? (
                                                    <div className="bg-neutral-50 p-3 hairline">
                                                        <Text size="sm" className="opacity-60">
                                                            Background information editing is available with Basic plan or above.
                                                        </Text>
                                                        <Link
                                                            to="/profile?tab=subscription"
                                                            className="inline-block mt-2 px-4 py-2 bg-neutral-900 text-neutral-0 hairline font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                                                        >
                                                            VIEW PLANS
                                                        </Link>
                                                    </div>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => setIsModalOpen(true)}
                                                        className="w-full px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:!text-purple-300 transition-colors font-mono text-sm uppercase tracking-wider"
                                                    >
                                                        {hasBackgroundInfo() ? 'EDIT BACKGROUND INFO' : 'ADD BACKGROUND INFO'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Premium Lock Popup */}
                        {showPremiumLockPopup && (
                            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center" onClick={() => setShowPremiumLockPopup(false)}>
                                <div className="bg-neutral-0 hairline p-6 max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
                                    <Heading level={3} className="text-xl font-medium mb-4" style={{ color: colorTokens.oxfordBlue }}>
                                        Premium Feature
                                    </Heading>
                                    <Text className="mb-4">
                                        Chef features are locked for premium users. Upgrade to Basic plan or above to unlock these features.
                                    </Text>
                                    <div className="flex gap-3">
                                        <Link
                                            to="/profile?tab=subscription"
                                            className="px-4 py-2 bg-neutral-900 text-neutral-0 hairline font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                                            onClick={() => setShowPremiumLockPopup(false)}
                                        >
                                            VIEW PLANS
                                        </Link>
                                        <button
                                            onClick={() => setShowPremiumLockPopup(false)}
                                            className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-900 hover:!text-purple-300 transition-colors"
                                        >
                                            CLOSE
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Section 6: Friends & Family */}
                        {isOwnProfile && (
                            <div className="mb-6">
                                <div className="bg-neutral-0 hairline minimal-padding">
                                    <Heading level={2} className="text-2xl font-medium mb-4" style={{ color: colorTokens.oxfordBlue }}>
                                        Friends & Family
                                    </Heading>
                                                
                                                <div className="space-y-4">
                                                    <Text>
                                                        Connect with friends and see their cooking activity on The Table.
                                                    </Text>
                                                    
                                                    <div className="p-4 bg-neutral-50 hairline">
                                                        <div className="text-center">
                                                            <Text className="font-mono text-sm uppercase tracking-wider opacity-60 mb-1">
                                                                YOUR CIRCLE
                                                            </Text>
                                                            <Text className="text-lg font-medium mb-3">
                                                                {friendsCount} {friendsCount === 1 ? 'Friend' : 'Friends'}
                                                            </Text>
                                                            <button
                                                                type="button"
                                                                onClick={() => setShowFriendsModal(true)}
                                                                className="w-full px-4 py-2 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-all"
                                                                style={{ backgroundColor: colorTokens.oxfordBlue, color: colorTokens.white }}
                                                            >
                                                                MANAGE FRIENDS
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                </div>
                            </div>
                        )}

                        {/* Section 7: Legal & Privacy */}
                        <div className="mb-6">
                            <div className="bg-neutral-0 hairline minimal-padding">
                                    <Heading level={2} className="text-2xl font-medium mb-4" style={{ color: colorTokens.oxfordBlue }}>
                                        Legal & Privacy
                                    </Heading>
                                <div className="space-y-3">
                                    <Link 
                                        to="/terms-of-service" 
                                        target="_blank"
                                        className="block text-neutral-900 hover:opacity-60 transition-opacity underline"
                                    >
                                        View Terms of Service
                                    </Link>
                                    <Link 
                                        to="/privacy-policy" 
                                        target="_blank"
                                        className="block text-neutral-900 hover:opacity-60 transition-opacity underline"
                                    >
                                        View Privacy Policy
                                    </Link>
                                    <Link 
                                        to="/dmca-policy" 
                                        target="_blank"
                                        className="block text-neutral-900 hover:opacity-60 transition-opacity underline"
                                    >
                                        View DMCA Policy
                                    </Link>
                                </div>
                            </div>
                        </div>

                        {/* Save Button */}
                        {isOwnProfile && (
                            <div className="mb-6">
                                <form onSubmit={handleSubmit}>
                                    <button
                                        type="submit"
                                        className="w-full px-6 py-3 bg-neutral-900 text-neutral-0 hairline font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                                    >
                                        UPDATE PROFILE
                                    </button>
                                </form>
                            </div>
                        )}
                    </>
                )}

                {/* Subscription Tab Content */}
                {activeTab === 'subscription' && isOwnProfile && (
                    <div className="mb-6">
                        <div className="bg-neutral-0 hairline p-6 min-h-[600px]">
                            {/* Pricing Tiers */}
                            <div className="mb-8">
                                <Heading level={2} className="text-2xl font-medium mb-4" style={{ color: colorTokens.oxfordBlue }}>
                                    Choose Your Plan
                                </Heading>
                                <AdvancedPricingTiers 
                                    currentPlan={
                                        userProfile?.is_admin ? 'admin' :
                                        userProfile?.is_family ? 'family' :
                                        userProfile?.is_beta ? 'beta' :
                                        subscription?.planType
                                    }
                                    onSelectPlan={handleUpgrade}
                                    loading={subscriptionLoading}
                                />
                            </div>

                            {/* Subscription Status */}
                            <div className="mb-8">
                                <div className="flex items-center justify-between mb-4">
                                    <Heading level={2} className="text-2xl font-medium" style={{ color: colorTokens.oxfordBlue }}>
                                        Subscription Status
                                    </Heading>
                                    <button
                                        onClick={fetchSubscriptionData}
                                        disabled={subscriptionLoading}
                                        className="px-3 py-1 bg-neutral-50 text-neutral-900 hairline font-mono text-xs uppercase tracking-wider hover:bg-neutral-900 hover:!text-purple-300 transition-colors disabled:opacity-50"
                                    >
                                        REFRESH
                                    </button>
                                </div>
                                
                                {subscriptionLoading ? (
                                    <div className="flex items-center justify-center py-8">
                                        <Text className="text-neutral-900 opacity-60">Loading subscription information...</Text>
                                    </div>
                                ) : subscription ? (
                                    <div className="space-y-4">
                                        <div className="bg-neutral-50 p-4 hairline">
                                            <div className="flex items-center justify-between mb-2">
                                                <Text className="font-mono text-sm uppercase tracking-wider opacity-60">CURRENT PLAN</Text>
                                                <Text className="text-lg font-medium">{getPlanDisplayName(subscription.planType)}</Text>
                                            </div>
                                            <div className="flex items-center justify-between mb-2">
                                                <Text className="font-mono text-sm uppercase tracking-wider opacity-60">RECIPE LIMIT</Text>
                                                <Text className="text-lg font-medium">Unlimited</Text>
                                            </div>
                                            {quotaInfo && (
                                                <div className="flex items-center justify-between mb-2">
                                                    <Text className="font-mono text-sm uppercase tracking-wider opacity-60">RECIPES CREATED</Text>
                                                    <Text className="text-lg font-medium">{quotaInfo.used || 0}</Text>
                                                </div>
                                            )}
                                            <div className="mt-3 p-3 bg-green-50 border border-green-200">
                                                <Text className="font-sans text-xs text-green-800">
                                                    ✨ All users can create unlimited recipes! Premium features like ingredient search and grocery lists are available with premium plans.
                                                </Text>
                                            </div>
                                            {subscription.status === 'active' && subscription.currentPeriodEnd && (
                                                <div className="flex items-center justify-between">
                                                    <Text className="font-mono text-sm uppercase tracking-wider opacity-60">NEXT BILLING</Text>
                                                    <Text className="text-lg font-medium">
                                                        {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                                                    </Text>
                                                </div>
                                            )}
                                        </div>

                                        {/* Special message for admin/family/beta users */}
                                        {(userProfile?.is_admin || userProfile?.is_family || userProfile?.is_beta) && (
                                            <div className="bg-white text-black p-4 hairline">
                                                <div className="flex items-center justify-between mb-2">
                                                    <Text className="font-mono text-sm uppercase tracking-wider text-black">ACCOUNT TYPE</Text>
                                                    <Text className="text-lg font-medium text-black">
                                                        {userProfile?.is_admin ? 'ADMIN' : 
                                                         userProfile?.is_family ? 'FAMILY' : 
                                                         userProfile?.is_beta ? 'BETA' : ''}
                                                    </Text>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <Text className="font-mono text-sm uppercase tracking-wider text-black">ACCESS</Text>
                                                    <Text className="text-lg font-medium text-black">UNLIMITED</Text>
                                                </div>
                                            </div>
                                        )}

                                        {/* Action Buttons */}
                                        <div className="flex flex-col sm:flex-row gap-3">
                                            {subscription.planType === 'free' && !userProfile?.is_admin && !userProfile?.is_family && !userProfile?.is_beta && (
                                                <button
                                                    onClick={() => handleUpgrade('basic')}
                                                    disabled={true}
                                                    className="flex-1 px-4 py-2 bg-neutral-300 text-neutral-500 hairline font-mono text-sm uppercase tracking-wider cursor-not-allowed"
                                                >
                                                    COMING SOON
                                                </button>
                                            )}
                                            {subscription.planType === 'basic' && !userProfile?.is_admin && !userProfile?.is_family && !userProfile?.is_beta && (
                                                <button
                                                    onClick={() => handleUpgrade('unlimited')}
                                                    disabled={true}
                                                    className="flex-1 px-4 py-2 bg-neutral-300 text-neutral-500 hairline font-mono text-sm uppercase tracking-wider cursor-not-allowed"
                                                >
                                                    COMING SOON
                                                </button>
                                            )}
                                            {subscription.planType !== 'free' && subscription.status === 'active' && (
                                                <button
                                                    onClick={handleCancelSubscription}
                                                    disabled={subscriptionLoading}
                                                    className="flex-1 px-4 py-2 bg-red-50 text-red-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-red-900 hover:text-red-50 transition-colors disabled:opacity-50"
                                                >
                                                    CANCEL SUBSCRIPTION
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ) : subscriptionError ? (
                                    <div className="bg-red-50 p-4 hairline">
                                        <Text className="text-red-900">{subscriptionError}</Text>
                                        <button
                                            onClick={fetchSubscriptionData}
                                            className="mt-2 px-3 py-1 bg-red-100 text-red-900 hairline font-mono text-xs uppercase tracking-wider hover:bg-red-900 hover:text-red-50 transition-colors"
                                        >
                                            RETRY
                                        </button>
                                    </div>
                                ) : (
                                    <div className="bg-yellow-50 p-4 hairline">
                                        <Text className="text-yellow-900">No subscription information available</Text>
                                    </div>
                                )}
                            </div>

                            {/* Error/Success Messages */}
                            {(subscriptionError || subscriptionSuccess) && (
                                <div className="mt-6">
                                    {subscriptionError && (
                                        <div className="bg-red-50 p-4 hairline mb-4">
                                            <Text className="text-red-900">{subscriptionError}</Text>
                                        </div>
                                    )}
                                    {subscriptionSuccess && (
                                        <div className="bg-green-50 p-4 hairline mb-4">
                                            <Text className="text-green-900">{subscriptionSuccess}</Text>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}


                {/* Background Info Modal */}
                <BackgroundInfoModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSave={handleBackgroundInfoSave}
                    currentInfo={profileData.backgroundInfo}
                />

                {/* Friends Modal */}
                <FriendsModal
                    isOpen={showFriendsModal}
                    onClose={async () => {
                        setShowFriendsModal(false);
                        // Refresh friends count when modal closes
                        if (user) {
                            try {
                                const token = await user.getIdToken();
                                const friends = await getFriends(token);
                                setFriendsCount(friends.length);
                            } catch (err) {
                                console.error('Error loading friends:', err);
                            }
                        }
                    }}
                />
            </div>
        </div>
    );
};

export default ProfilePage; 
